import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { Building2, Server, Gamepad2, Activity, TrendingUp, Receipt, Loader2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const peso = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(value || 0);

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="card-surface p-5 fade-up" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">{label}</div>
          <div className="mt-2 text-3xl font-semibold font-mono tracking-tight" style={accent ? { color: "var(--gold)" } : {}}>
            {value}
          </div>
          {sub && <div className="text-xs text-[var(--text-2)] mt-1">{sub}</div>}
        </div>
        {Icon && <Icon size={18} className="text-[var(--text-3)]"/>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/admin/dashboard")
       .then(({ data }) => setData(data))
       .catch((e) => setErr(String(e?.response?.data?.detail || e.message)));
  }, []);

  if (err) return <div className="text-[var(--error)]" data-testid="dashboard-error">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-[var(--text-2)]"><Loader2 className="animate-spin" size={14}/> Loading…</div>;

  const max = Math.max(1, ...data.transactions_7d.map(d => d.count));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Operators" value={data.operators} icon={Building2}/>
        <StatCard label="Providers" value={data.providers} icon={Server}/>
        <StatCard label="Games" value={data.games} icon={Gamepad2}/>
        <StatCard label="Transactions" value={data.transactions.toLocaleString()} icon={Receipt}/>
        <StatCard
          label="GGR"
          value={peso(data.ggr)}
          sub={`Bets ${peso(data.bets_total)} · Wins ${peso(data.wins_total)}`}
          icon={TrendingUp}
          accent
        />
        <StatCard
          label="NGR"
          value={peso(data.ngr)}
          sub="Mocked margin estimate"
          icon={TrendingUp}
        />
        <StatCard label="Active Sessions" value={data.active_sessions} icon={Activity}/>
        <StatCard label="Platform Version" value="v1.4.0" sub="MVP · MongoDB" icon={ArrowUpRight}/>
      </div>

      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">Transactions · last 7 days</div>
            <div className="text-lg font-semibold mt-0.5">Volume trend</div>
          </div>
          <Link to="/wallet" className="text-xs text-[var(--gold)] hover:underline">View ledger →</Link>
        </div>

        <div className="flex items-end gap-2 h-40">
          {data.transactions_7d.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
              <div
                className="w-full rounded-t bg-[var(--gold)] opacity-80 hover:opacity-100 transition-all"
                style={{ height: `${(d.count / max) * 100}%`, minHeight: "4px" }}
                title={`${d.date}: ${d.count}`}
              />
              <div className="text-[10px] text-[var(--text-3)] font-mono">{d.date.slice(5)}</div>
              <div className="text-xs font-mono">{d.count}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-surface p-0 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
            <div className="text-sm font-semibold">Recent transactions</div>
            <Link to="/wallet" className="text-xs text-[var(--gold)]">All →</Link>
          </div>

          <div className="overflow-x-auto">
            <table className="aggr-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Player</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_transactions.map(t => (
                  <tr key={t.id}>
                    <td><StatusBadge status={t.txn_type}/></td>
                    <td className="font-mono">{peso(t.amount)}</td>
                    <td className="font-mono text-xs">{t.player_id}</td>
                    <td className="font-mono text-xs">{peso(t.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-surface p-0 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
            <div className="text-sm font-semibold">Recent audit activity</div>
            <Link to="/audit" className="text-xs text-[var(--gold)]">All →</Link>
          </div>

          <div className="overflow-x-auto">
            <table className="aggr-table">
              <thead>
                <tr>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_audit.map(a => (
                  <tr key={a.id}>
                    <td className="text-xs">{a.actor_email}</td>
                    <td><span className="font-mono text-xs text-[var(--gold)]">{a.action}</span></td>
                    <td className="text-xs">{a.resource_type}</td>
                    <td className="text-xs text-[var(--text-3)]">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
